import React, { useMemo, useState } from "react"
import { ChevronRightIcon, MagnifyingGlassIcon } from "@heroicons/react/24/outline"
import Typography from "aldoo-ra/Typography"
import Button from "aldoo-ra/Button"
import Input from "aldoo-ra/Input"
import useT from "../../hooks/useT"
import { useCategories, useContentTypes } from "../../hooks/useCatalogData"
import { buildCategoryUrl, categoryUrlOf, childCategories, findCategory, hasProducts, isRootCategory } from "../../utils/categories"
import { useCategoryTrail } from "../../hooks/useCategoryTrail"

// a list longer than this gets a search box and a fixed height that scrolls
const LONG_LIST = 10

const norm = (value) => String(value || "").toLocaleLowerCase("bg")

/**
 * A sidebar list of links. A long one (an imported catalog brings dozens of
 * categories) is a box of fixed height with a search on top, as the big
 * stores show their filters — links, not checkboxes: a page is one category.
 */
function ColumnList({ title, items, searchPlaceholder, sheet = false, filter = "" }) {
  const [query, setQuery] = useState("")
  // in the phone's sheet the search is the sheet's own (`filter`), fixed over
  // the lists; the list scrolls with the sheet
  const long = sheet || items.length > LONG_LIST
  const shown = useMemo(() => {
    const term = norm((sheet ? filter : query).trim())
    if (!long || !term) return items
    return items.filter(({ name }) => norm(name).includes(term))
  }, [items, query, filter, long, sheet])
  if (items.length === 0) return null
  // the sheet's title is the modal's; a section keeps a small label only
  // when it is not the first (the product types under the categories)
  if (sheet && shown.length === 0) return null
  return (
    <div className="w-full" data-testid="category-column" data-long={long ? "1" : undefined}>
      {sheet ? (
        // a section after the first (the product types) starts with a coloured
        // rule and its label, so the two lists read apart
        title && (
          <div className="border-t border-theme_white/20 bg-paper/5 px-5 pt-3 pb-2 mt-4 mb-2 -mx-5">
            <Typography 
              variant="label_upper_dim" 
              className="text-text_inv/70"
            >
              {title}
            </Typography>
          </div>
        )
      ) : (
        <Typography component="h2" variant="h5" className="mb-2">
          {title}
        </Typography>
      )}
      
      {long && !sheet && (
        <div className="mb-3" data-testid="category-column-search">
          <Input
            variant="aldoo"
            type="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={searchPlaceholder}
            icon={<MagnifyingGlassIcon className="w-4 h-4 text-text_secondary" />}
          />
        </div>
      )}

      <div className={long && !sheet ? "flex flex-col gap-1 max-h-[360px] overflow-y-auto pr-1 scrollbar-thin scrollbar-thumb-neutral_2 scrollbar-track-transparent" : "flex flex-col gap-1"}>
        {shown.map(({ key, url, name }) => (
          <Button 
            variant="none" 
            key={key} 
            href={url} 
            className={sheet ? "flex items-center h-11 px-2 rounded-lg text-white hover:bg-white/10 active:bg-white/10 transition-colors" : "flex items-center pb-2 px-2 rounded hover:text-primary transition-colors"}
          >
            <ChevronRightIcon className="w-3 h-3 text-primary stroke-2 mr-1.5 shrink-0" />
            <span className={sheet ? "text-base" : "text-sm"}>
              {name}
            </span>
          </Button>
        ))}

        {long && shown.length === 0 && <span className={"text-sm px-2 py-1 " + (sheet ? "text-neutral_1" : "text-text_secondary")}>—</span>}
      </div>
      {!sheet && <hr className="mt-4 border-bordered" />}
    </div>
  )
}

/**
 * Left sidebar list: subcategories of the current category, else its
 * siblings, else the root categories.
 */
export function CategoryColumn({ parentCategoryId, sheet = false, filter = "" }) {
  const t = useT()
  const { list } = useCategories()
  // the path the visitor came down: the links go on from it, so a category
  // under two parents keeps the one it was reached by
  const trail = useCategoryTrail()
  const here = trail && trail[trail.length - 1].categoryId === parentCategoryId ? trail : null

  const categories = useMemo(() => {
    if (!list.length) return []
    const roots = list.filter((c) => isRootCategory(c) && hasProducts(c)).map((c) => ({ ...c, url: buildCategoryUrl(c.categoryId, list) }))
    if (!parentCategoryId) return roots
    const subs = childCategories(list, parentCategoryId).filter(hasProducts)
    if (subs.length > 0) return subs.map((c) => ({ ...c, url: here ? categoryUrlOf([...here, c]) : buildCategoryUrl(c.categoryId, list) }))
    const current = findCategory(list, parentCategoryId)
    const parent = here && here.length > 1 ? here[here.length - 2].categoryId : current?.parentCategoryIds?.[0] || current?.parentCategoryId
    if (current && parent) return childCategories(list, parent).filter(hasProducts).map((c) => ({ ...c, url: here && here.length > 1 ? categoryUrlOf([...here.slice(0, -1), c]) : buildCategoryUrl(c.categoryId, list) }))
    return roots
  }, [list, parentCategoryId, here?.map((c) => c.categoryId).join("/")])

  return (
    <ColumnList
      sheet={sheet}
      filter={filter}
      title={sheet ? "" : t`Categories`}
      searchPlaceholder={t`Category search`}
      items={categories.map((c) => ({ key: c.categoryId, url: c.url, name: c.name }))}
    />
  )
}

// Left sidebar list of product types (/type/:contentTypeId)
export function ContentTypeColumn({ sheet = false, filter = "" }) {
  const t = useT()
  const { list } = useContentTypes()
  const types = useMemo(
    () =>
      [...list]
        .filter((ct) => ct.productCount === undefined || ct.productCount > 0)
        .sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0) || (a.name || "").localeCompare(b.name || "")),
    [list]
  )
  return (
    <div className={sheet ? "" : "mt-6"}>
      <ColumnList 
        sheet={sheet} 
        filter={filter} 
        title={t`Product Types`} 
        searchPlaceholder={t`Search`} 
        items={types.map((ct) => ({ key: ct.contentId, url: `/type/${ct.contentId}`, name: ct.name }))} 
      />
    </div>
  )
}
